import React, { useState, useLayoutEffect, useRef } from 'react'
import styles from './styles.module.scss'

import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import SignatureCanvas from 'react-signature-canvas'
import axios from 'axios'
import { SubmitButton } from '..'
import { useParams } from 'react-router-dom'
import { consentWordingV3, usesMotorFinanceConsentV3 } from '../../../data/consentWording'

interface sigProps {
    additionalAction?: (obj?: any) => void | null
    extraPayload: any
    disclaimer?: string
    consentText?: string
    additionalDisclosure?: {
        content: React.ReactNode
        confirmationLabel?: string
    }
    // Optional overrides for the two confirmation checkboxes. The defaults are
    // the CFA/LOA wording used by the motor finance flows; generic flows (e.g.
    // contract signing) pass their own wording.
    agreementLabel?: React.ReactNode
    privacyLabel?: React.ReactNode
}

export const SignaturePad = ({
    extraPayload,
    additionalAction,
    disclaimer,
    consentText,
    additionalDisclosure,
    agreementLabel,
    privacyLabel,
}: sigProps) => {
    const signCanvas = React.useRef() as React.MutableRefObject<any>
    const canvasWrapperRef = useRef<HTMLDivElement>(null)
    const [canvasWidth, setCanvasWidth] = useState(350)
    const [sigData, setSigData] = useState('')
    const [checkbox1Checked, setCheckbox1Checked] = useState(false)
    const [checkbox2Checked, setCheckbox2Checked] = useState(false)
    const [noDuplicateClaimConsentChecked, setNoDuplicateClaimConsentChecked] = useState(false)
    const [additionalDisclosureChecked, setAdditionalDisclosureChecked] = useState(false)
    const [v3ConsentChecks, setV3ConsentChecks] = useState<Record<string, boolean>>(() =>
        consentWordingV3.reduce((checks, item) => ({ ...checks, [item.fieldName]: false }), {})
    )

    const { token } = useParams()
    const requiresNoDuplicateClaimConsent = Object.prototype.hasOwnProperty.call(extraPayload || {}, 'noDuplicateClaimConsent')
    const additionalDisclosureConfirmationLabel = additionalDisclosure?.confirmationLabel
    const requiresAdditionalDisclosureConsent = Boolean(additionalDisclosureConfirmationLabel)
    const usesV3Consent = usesMotorFinanceConsentV3(extraPayload?.type)
    const allV3ConsentChecked = consentWordingV3.every((item) => v3ConsentChecks[item.fieldName])

    // Size the canvas to its container so it never overflows the viewport (and so
    // the drawing bitmap matches the rendered size, keeping pen coordinates aligned).
    useLayoutEffect(() => {
        const el = canvasWrapperRef.current
        if (!el) return

        const updateWidth = () => {
            const width = el.clientWidth
            if (width > 0) setCanvasWidth(width)
        }

        updateWidth()

        const resizeObserver = new ResizeObserver(updateWidth)
        resizeObserver.observe(el)
        window.addEventListener('resize', updateWidth)

        return () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', updateWidth)
        }
    }, [])

    const saveSign = () => {
        signCanvas.current.on()
        let x = signCanvas.current.toDataURL('image/png')
        setSigData(x)
    }

    const clearSign = (e: React.MouseEvent) => {
        e.preventDefault()
        clearSigPad()
    }

    const clearSigPad = () => {
        signCanvas.current.clear()
        signCanvas.current.on()
        setSigData('')
    }

    const submitSignature = () => {
        if (requiresAdditionalDisclosureConsent && !additionalDisclosureChecked) {
            Report.failure(
                'Please Confirm Declaration',
                'Please confirm that you have read and understood the information above before continuing.',
                'OK'
            )
            return
        }

        if (!usesV3Consent && requiresNoDuplicateClaimConsent && !noDuplicateClaimConsentChecked) {
            Report.failure(
                'Please Confirm Declaration',
                'Please confirm that you have not instructed another law firm or claims management company for the same finance agreement(s).',
                'OK'
            )
            return
        }

        if (sigData.length < 1) {
            Report.failure('Please Sign', 'Please enter your signature onto the signature pad to continue.', 'OK')
            return
        }

        Loading.circle('Signing Document')
        let payload = {
            token,
            type: '',
            sigData: sigData,
        }

        // console.log('extra payload: ', extraPayload)
        // console.log('extra payload: ', Object.keys(extraPayload).length)

        if (extraPayload.signerData && Object.values(extraPayload.signerData).some((e: any) => !e.length)) {
            Report.failure('Error', 'Please complete all fields.', 'Okay')
            return
        }
        if (extraPayload.type == 'dsar-loa-dob-conf') {
            let c = extraPayload.clientData
            if (
                !c.dob ||
                !c.address_line_one ||
                !c.postcode ||
                c.dob == '' ||
                c.dob == '23/01/2007' ||
                c.address_line_one == '' ||
                c.postcode == ''
            ) {
                Loading.remove()
                Report.failure('Error', 'Please update your details to complete this form.', 'Okay')
                return
            }
        }

        if (extraPayload.type == 'loa-options') {
            let clientSelection = extraPayload.clientSelection
            if (!clientSelection || clientSelection == '') {
                Loading.remove()
                Report.failure('Error', 'Please select an option above to continue.', 'Okay')
                return
            }
        }

        if (extraPayload && Object.keys(extraPayload).length) {
            payload = {
                ...payload,
                ...extraPayload,
                ...(requiresNoDuplicateClaimConsent
                    ? {
                          noDuplicateClaimConsent: usesV3Consent
                              ? v3ConsentChecks.sentinelOnlyFirm
                              : noDuplicateClaimConsentChecked,
                      }
                    : {}),
            }
        }

        axios
            .post(`${process.env.REACT_APP_API_UPLOAD_URL}`, payload)
            .then(function (response) {
                console.log(response)
                Loading.remove()
                if (response.data !== 'SUCCESS' && response.data?.success !== true) {
                    Report.failure(
                        'An Error Occurred',
                        'We have encounterred an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk',
                        'Okay'
                    )
                    return
                }
                clearSigPad()

                if (additionalAction && extraPayload.type == 'lc-cfa-signature-il') {
                    additionalAction(response.data)
                } else if (additionalAction) {
                    additionalAction()
                }
            })
            .catch(function (error) {
                console.log(error)
                Loading.remove()
                Report.failure(
                    'An Error Occurred',
                    'We have encounterred an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk ',
                    'Okay'
                )
                // if (additionalAction) additionalAction();
            })
    }

    //<Previewer url={preview} />;
    return (
        <div className={styles.signaturePadContent}>
            <div style={{ textAlign: 'center', fontWeight: 'bold' }}>Please sign your name in BLOCK CAPITALS</div>
            <div className={`${styles.sigPadContainer}  `}>
                <p>Please use your mouse, finger or stylus to draw your signature below.</p>
                <div ref={canvasWrapperRef}>
                    <SignatureCanvas
                        ref={signCanvas}
                        onEnd={saveSign}
                        clearOnResize={false}
                        penColor="grey"
                        canvasProps={{
                            width: canvasWidth,
                            height: 300,
                            className: 'sigCanvas',
                        }}
                    />
                    <span onClick={clearSign}>Clear</span>
                </div>
            </div>

            {!usesV3Consent && disclaimer ? (
                <p
                    style={{
                        fontSize: '12px',
                        textAlign: 'left',
                        color: 'grey',
                        maxWidth: '800px',
                        margin: '20px auto',
                    }}
                >
                    {disclaimer}
                </p>
            ) : (
                ''
            )}
            {!usesV3Consent && consentText ? (
                <p
                    style={{
                        fontSize: '12px',
                        textAlign: 'left',
                        color: 'grey',
                        maxWidth: '800px',
                        margin: '20px auto',
                    }}
                >
                    <div dangerouslySetInnerHTML={{ __html: consentText }} />
                </p>
            ) : (
                ''
            )}
            {additionalDisclosure ? (
                <div
                    style={{
                        fontSize: '12px',
                        textAlign: 'left',
                        color: 'grey',
                        maxWidth: '800px',
                        margin: '20px auto',
                        lineHeight: '1.4',
                    }}
                >
                    {additionalDisclosure.content}
                </div>
            ) : (
                ''
            )}
            {usesV3Consent ? (
                <section className={styles.consentDisclosure}>
                    <h3>ViewThru consent</h3>
                    <p>
                        I understand that a number of entities working together are involved in gathering and considering
                        information to assess my anticipated claim. I therefore understand and agree that:
                    </p>
                    <ul>
                        <li>
                            ViewThru will carry out a ‘soft’ credit search about me with Credit Reference Agency Equifax
                            Limited, and the results will be shared with Sentinel Legal for purposes of considering my
                            anticipated claim. I’m aware that this will not impact my credit score.
                        </li>
                        <li>
                            For the purpose of proceeding with the anticipated claim, I acknowledge that I am required to
                            be based within the United Kingdom. Therefore, as part of assessing my eligibility for the
                            anticipated claim, my IP address and/or mobile phone location data may be used to confirm that
                            I am in the United Kingdom. These results will also be shared with Sentinel Legal.
                        </li>
                        <li>
                            I understand that I may withdraw my consent at any time in respect of the above by contacting
                            ViewThru, and this will not affect processing carried out before I withdrew consent.
                        </li>
                    </ul>
                    <p>
                        I acknowledge that I have read and agreed to{' '}
                        <a href="https://www.viewthru.co.uk/privacy" target="_blank" rel="noreferrer">
                            ViewThru’s Privacy Policy
                        </a>
                        .{' '}
                        <a
                            href="https://www.sentinellegal.co.uk/privacy-policy"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Sentinel Legal’s Privacy Policy
                        </a>{' '}
                        can also be accessed here.
                    </p>
                    <p>
                        By clicking the button below, I give my acknowledgement and consent to the above provisions
                        freely, expressly, and on an informed basis.
                    </p>
                </section>
            ) : (
                ''
            )}
            <div style={{ margin: '20px auto', maxWidth: '800px', textAlign: 'left' }}>
                {requiresAdditionalDisclosureConsent ? (
                    <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <input
                            type="checkbox"
                            id="additionalDisclosureCheckbox"
                            checked={additionalDisclosureChecked}
                            onChange={(e) => setAdditionalDisclosureChecked(e.target.checked)}
                            style={{ marginTop: '2px' }}
                        />
                        <label
                            htmlFor="additionalDisclosureCheckbox"
                            style={{ fontSize: '12px', color: 'grey', lineHeight: '1.4' }}
                        >
                            {additionalDisclosureConfirmationLabel}
                        </label>
                    </div>
                ) : (
                    ''
                )}
                {!usesV3Consent ? (
                    <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <input
                            type="checkbox"
                            id="checkbox1"
                            checked={checkbox1Checked}
                            onChange={(e) => setCheckbox1Checked(e.target.checked)}
                            style={{ marginTop: '2px' }}
                        />
                        <label htmlFor="checkbox1" style={{ fontSize: '12px', color: 'grey', lineHeight: '1.4' }}>
                            {agreementLabel ?? (
                                <>
                                    I confirm that I have read, understood and agree to the Conditional Fee Agreement
                                    and Letter of Authority documents above. I authorize Sentinel Legal to act on my
                                    behalf and to apply my signature to these documents for the purpose of submitting
                                    my claim.
                                </>
                            )}
                        </label>
                    </div>
                ) : (
                    ''
                )}
                {usesV3Consent
                    ? consentWordingV3.map((item) => (
                          <div
                              key={item.fieldName}
                              style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}
                          >
                              <input
                                  type="checkbox"
                                  id={`v3-${item.fieldName}`}
                                  checked={v3ConsentChecks[item.fieldName]}
                                  onChange={(event) =>
                                      setV3ConsentChecks((checks) => ({
                                          ...checks,
                                          [item.fieldName]: event.target.checked,
                                      }))
                                  }
                                  style={{ marginTop: '2px' }}
                              />
                              <label
                                  htmlFor={`v3-${item.fieldName}`}
                                  style={{ fontSize: '12px', color: 'grey', lineHeight: '1.4' }}
                              >
                                  <strong>{item.emphasisedText}</strong>
                                  {item.label.slice(item.emphasisedText.length)}
                              </label>
                          </div>
                      ))
                    : (
                          <div
                              style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}
                          >
                              <input
                                  type="checkbox"
                                  id="checkbox2"
                                  checked={checkbox2Checked}
                                  onChange={(e) => setCheckbox2Checked(e.target.checked)}
                                  style={{ marginTop: '2px' }}
                              />
                              <label
                                  htmlFor="checkbox2"
                                  style={{ fontSize: '12px', color: 'grey', lineHeight: '1.4' }}
                              >
                                  {privacyLabel ?? (
                                      <>
                                          I agree to the privacy policy of Sentinel Legal and authorise them to conduct
                                          soft credit searches on my behalf through their provider, Valid8 IP Ltd, with
                                          no impact on my credit score. I also agree to{' '}
                                          <span
                                              style={{
                                                  color: 'grey',
                                                  fontSize: '12px',
                                                  textDecoration: 'underline',
                                              }}
                                          >
                                              <a
                                                  href="https://valid8.co.uk/privacy.php"
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{
                                                      color: 'grey',
                                                      fontSize: '12px',
                                                      textDecoration: 'underline',
                                                  }}
                                              >
                                                  Valid8 IP's privacy policy
                                              </a>
                                          </span>
                                          .
                                      </>
                                  )}
                              </label>
                          </div>
                      )}
                {!usesV3Consent && requiresNoDuplicateClaimConsent ? (
                    <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <input
                            type="checkbox"
                            id="noDuplicateClaimConsent"
                            checked={noDuplicateClaimConsentChecked}
                            onChange={(e) => setNoDuplicateClaimConsentChecked(e.target.checked)}
                            style={{ marginTop: '2px' }}
                        />
                        <label
                            htmlFor="noDuplicateClaimConsent"
                            style={{ fontSize: '12px', color: 'grey', lineHeight: '1.4' }}
                        >
                            I confirm that I have not previously instructed, nor am I currently instructing, any other
                            law firm or claims management company to pursue a motor finance claim on my behalf in
                            relation to the same finance agreement(s). I understand that submitting duplicate claims may
                            delay or prejudice the outcome of my case.
                        </label>
                    </div>
                ) : (
                    ''
                )}
            </div>
            <div className={styles.buttonContainer}>
                <SubmitButton
                    text="Submit"
                    btnType="primary"
                    onClick={submitSignature}
                    disabled={
                        (!usesV3Consent && !checkbox1Checked) ||
                        (usesV3Consent ? !allV3ConsentChecked : !checkbox2Checked) ||
                        (!usesV3Consent &&
                            requiresNoDuplicateClaimConsent &&
                            !noDuplicateClaimConsentChecked) ||
                        (requiresAdditionalDisclosureConsent && !additionalDisclosureChecked)
                    }
                />
            </div>
        </div>
    )
}
